import {
  pgTable,
  text,
  timestamp,
  uuid,
  bigint,
  jsonb,
  index,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Event types
// ---------------------------------------------------------------------------

export const PAGE_BUILDER_EVENT_TYPES = [
  "page.created",
  "page.updated",
  "page.publish_scheduled",
  "page.publish_schedule_cancelled",
  "page.published",
  "page.unpublished",
  "page.archived",
  "page.restored",
  "page.duplicated",
  "page.deleted",
  "version.created",
  "version.restored",
  "review.created",
  "review.comment_added",
  "review.comment_resolved",
  "review.approved",
  "review.changes_requested",
  "form.submitted",
  "form.processed",
  "domain.registered",
  "domain.verified",
  "domain.failed",
  "domain.removed",
  "domain.mapped",
  // DOM-010 Phase 7 — emitted by the api-pages provisionSsl writer + the
  // unified domain-verifier worker on every legal CF Custom Hostnames SSL
  // state transition. Payload contract pinned by
  // `dom-010-custom-hostname-ssl-event-catalog.ts`
  // (`Dom010SslStateChangedPayload`).
  "domain.ssl_state_changed",
  "asset.uploaded",
  "asset.deleted",
  "section.saved",
  "section.deleted",
  "section.used",
  "style_preset.saved",
  "style_preset.deleted",
  "page.comment_added",
  "page.comment_updated",
  "page.comment_resolved",
  "brand.updated",
  "folder.created",
  "folder.updated",
  "folder.deleted",
  "field_registry.synced",
  "field_registry.deleted",
  "generation.completed",
  "generation.failed",
  "generation.checkpoint_created",
  "generation.version_created",
  "generation.checkpoint_approved",
  "generation.checkpoint_rejected",
  "generation.checkpoint_reverted",
  "generation.checkpoint_expired",
  "audit.completed",
  "site.created",
  "site.updated",
  "site.publish_started",
  "site.published",
  "site.unpublished",
  "site.deleted",
  "blog.post_created",
  "blog.post_updated",
  "blog.post_scheduled",
  "blog.post_published",
  "blog.post_unpublished",
  "blog.post_deleted",
  "funnel.step_created",
  "funnel.step_updated",
  "funnel.step_reordered",
  "funnel.step_deleted",
  "funnel.published",
  "funnel.step_completed",
  "funnel.completed",
  "funnel.abandoned",
  "ab.experiment_started",
  "ab.experiment_concluded",
  "ab.winner_declared",
  "ab.experiment_paused",
  "ab.experiment_resumed",
  "ab.impression",
  "audit.poor_performance",
  "gdpr.erasure_completed",
] as const;

export const EVENT_ORIGINS = ["user", "system", "ai"] as const;

export type PageBuilderEventType = (typeof PAGE_BUILDER_EVENT_TYPES)[number];
export type EventOrigin = (typeof EVENT_ORIGINS)[number];

// ---------------------------------------------------------------------------
// Page Builder Events (CQRS)
// ---------------------------------------------------------------------------

export const pageBuilderEvents = pgTable(
  "page_builder_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    pageId: uuid("page_id"),
    eventType: text("event_type").notNull(),
    payload: jsonb("payload")
      .$type<Record<string, unknown>>()
      .notNull()
      .default({}),
    origin: text("origin").notNull(),
    actorId: text("actor_id").notNull(),
    sequenceNumber: bigint("sequence_number", { mode: "number" }).notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_pb_events_tenant_page_seq").on(
      table.tenantId,
      table.pageId,
      table.sequenceNumber,
    ),
    index("idx_pb_events_tenant_type").on(table.tenantId, table.eventType),
    // NOTE: A unique constraint on (pageId, sequenceNumber) is ineffective
    // because pageId is nullable. Postgres treats each NULL as distinct, so
    // the constraint silently allows duplicates for events without a page.
    // Fix via raw SQL migration:
    //   CREATE UNIQUE INDEX uq_pb_events_page_sequence
    //     ON page_builder_events (page_id, sequence_number)
    //     WHERE page_id IS NOT NULL;
    check(
      "chk_pb_events_origin",
      sql`${table.origin} IN ('user', 'system', 'ai')`,
    ),
    check(
      "chk_pb_events_event_type",
      sql`${table.eventType} IN (${sql.raw(PAGE_BUILDER_EVENT_TYPES.map((t) => `'${t}'`).join(", "))})`,
    ),
  ],
);
